import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "done", label: "Done" },
];
const normalize = (s) => (s === "completed" ? "done" : s === "not_started" ? "todo" : s === "blocked" ? "in_progress" : s);

export default function PartnerTasks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/portal/tasks"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    setSaving(id);
    try {
      await api.patch(`/portal/tasks/${id}`, { status });
      setItems((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
      toast.success("Task updated");
    } catch { toast.error("Could not update task"); }
    finally { setSaving(null); }
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-[#252221] mb-1">Tasks</h2>
      <p className="text-sm text-muted-foreground mb-5">Tasks assigned to you. Update the status as you make progress.</p>
      {loading ? <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div> :
        items.length === 0 ? <Card className="p-10 text-center text-muted-foreground border-[#E6EDF2]" data-testid="partner-tasks-empty">No tasks assigned to you right now.</Card> : (
          <div className="space-y-3">
            {items.map((t) => (
              <Card key={t.id} className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="partner-task-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#252221]">{t.title}</div>
                    {t.description && <div className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{t.description}</div>}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      {t.family_name && <span>{t.family_name}</span>}
                      {t.due_date && <span>· Due {formatDate(t.due_date)}</span>}
                      {t.priority && <StatusBadge value={t.priority} />}
                    </div>
                  </div>
                  <div className="w-[150px] shrink-0">
                    <Select value={normalize(t.status)} onValueChange={(v) => update(t.id, v)} disabled={saving === t.id}>
                      <SelectTrigger data-testid="partner-task-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
